import { BarChart3, ChevronDown, Download, FileText, Upload, Zap } from 'lucide-react'
import React, { type ComponentProps, useState } from 'react'

import { Banner, Button, DropdownMenu, FileUpload, Select } from 'slab'
import { getGetAdminMedicalPlanProjectionsDownloadProjectionsUrl, getGetAdminMedicalPlanProjectionsDownloadTemplateUrl, postAdminMedicalPlanProjectionsReplaceProjections, usePostAdminMedicalPlanProjectionsGenerateProjectedPrices } from 'utils/web_api'

import MedicalPlanProjectionsTable from './MedicalPlanProjectionsTable'

const MedicalPlanProjections: React.FC = () => {
  const currentYear = new Date().getFullYear()
  const [selectedYear, setSelectedYear] = useState<number>(currentYear)
  const [alert, setAlert] = useState<ComponentProps<typeof Banner> | null>(null)
  const [isDownloadDropdownOpen, setIsDownloadDropdownOpen] = useState<boolean>(false)
  const [showFileUpload, setShowFileUpload] = useState<boolean>(false)

  // Mutation for generating projected prices
  const generateProjectedPricesMutation = usePostAdminMedicalPlanProjectionsGenerateProjectedPrices()

  // Generate years from 2025 to current year
  const years = Array.from({ length: currentYear - 2025 + 1 }, (_, i) => 2025 + i).reverse()

  // Handler functions
  const handleDownloadProjections = () => {
    try {
      const projectionsUrl = getGetAdminMedicalPlanProjectionsDownloadProjectionsUrl({ year: selectedYear })
      window.open(projectionsUrl, '_blank')
      setAlert({ type: 'success', title: `Projections for ${selectedYear} downloaded.` })
    } catch {
      setAlert({ type: 'critical', title: 'Failed to download projections. Please try again.' })
    }
    setIsDownloadDropdownOpen(false)
  }

  const handleDownloadTemplate = () => {
    try {
      const templateUrl = getGetAdminMedicalPlanProjectionsDownloadTemplateUrl()
      window.open(templateUrl, '_blank')
      setAlert({
        type: 'success',
        title: `CSV template for ${selectedYear} downloaded.`,
        children: <p>Note: the 'carrier_name' column is not required, but has been provided in the template for ease of use.</p>
      })
    } catch {
      setAlert({ type: 'critical', title: 'Failed to download CSV template. Please try again.' })
    }
    setIsDownloadDropdownOpen(false)
  }

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setAlert({ type: 'info', title: `Uploading ${file.name} for ${selectedYear}...` })
      setShowFileUpload(false)

      try {
        // Create FormData for multipart/form-data request
        const formData = new FormData()
        formData.append('year', selectedYear.toString())
        formData.append('file', file)

        // Call the API
        const response = await postAdminMedicalPlanProjectionsReplaceProjections({
          body: formData,
        })

        if (response.errors && response.errors.length > 0) {
          const title = `Failed to upload ${file.name}`
          const children = (
            <div>
              <p>Errors: {response.errors.join(', ')}</p>
              {response.warnings && (<p>Warnings: {response.warnings?.join(', ')}</p>)}
            </div>
          )

          setAlert({
            type: 'critical',
            title,
            children,
          })
        } else {
          // Build success message with created and replaced counts
          const title = `Successfully uploaded ${file.name}`
          const children = (
            <div>
              {response.replaced_count > 0 && (<p>{response.replaced_count} projections replaced with {response.created_count} new projections for {selectedYear}.</p>)}
              {response.replaced_count === 0 && (<p>{response.created_count} new projections created for {selectedYear}.</p>)}
              {response.warnings && response.warnings.length > 0 && (<p>Warnings: {response.warnings.join(', ')}</p>)}
              <br />
              <p>
                Projections will be applied to the 'insurance_medical_plan_prices' collection when you click 'Generate Projected Prices'.
                They will also be applied the next time you ingest plan data for {selectedYear}.
              </p>
            </div>
          )

          // Check if there are warnings (validation might have passed but with issues)
          const alertType = response.warnings && response.warnings.length > 0 ? 'warning' : 'success'

          setAlert({
            type: alertType,
            title,
            children,
          })
        }
      } catch (error) {
        const errorMessage = error instanceof Error
          ? error.message
          : (error && typeof error === 'object' && 'message' in error
              ? String(error.message)
              : String(error))
        setAlert({
          type: 'critical',
          title: `Failed to upload ${file.name}`,
          children: (
            <div>
              <p>{errorMessage}</p>
              <p>Please check the file format and try again.</p>
            </div>
          ),
        })
      }
    }
  }

  const handleGenerateProjections = async () => {
    setAlert({ type: 'info', title: `Generating price projections for ${selectedYear}...` })

    try {
      const response = await generateProjectedPricesMutation.mutateAsync({
        data: { year: selectedYear }
      })

      const children = (
        <div>
          {response.warnings && response.warnings.length > 0 && (<p>Warnings: {response.warnings.join(', ')}</p>)}
          <br />
          <p>
            Follow along with progress on <a href={response.job_url} target='_blank' rel='noreferrer'>goodjob</a>.
          </p>
        </div>
      )

      // Show success or warning alert based on presence of warnings
      const alertType = response.warnings && response.warnings.length > 0 ? 'warning' : 'success'
      setAlert({
        type: alertType,
        title: response.message,
        children,
      })
    } catch (error) {
      const errorMessage = error instanceof Error
        ? error.message
        : (error && typeof error === 'object' && 'message' in error
            ? String(error.message)
            : String(error))
      setAlert({
        type: 'critical',
        title: `Failed to generate projections for ${selectedYear}`,
        children: (
          <div>
            <p>{errorMessage}</p>
          </div>
        ),
      })
    }
  }

  const handleReplaceProjectionsClick = () => {
    setShowFileUpload(true)
  }

  const dismissAlert = () => {
    setAlert(null)
  }

  return (
    <div className='p-6 space-y-6'>
      {/* Header section */}
      <div className='flex items-start justify-between'>
        <div>
          <h1 className='text-2xl font-bold text-gray-900'>Medical Plan Projections</h1>
          <p className='mt-2 text-sm text-gray-600'>
            View and filter medical plan projections by year, state, and carrier.
          </p>
        </div>
        <div className='ml-6'>
          <Select
            id='year'
            label='Projecting from'
            value={selectedYear.toString()}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            required
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {/* Alert banner */}
      {alert && (
        <Banner
          type={alert.type}
          title={alert.title}
          onDismiss={dismissAlert}
        >
          {alert.children}
        </Banner>
      )}

      {/* Actions */}
      <div className='bg-white shadow rounded-lg p-6'>
        <h2 className='text-lg font-medium text-gray-900 mb-4'>Actions</h2>
        <div className='flex items-center space-x-4'>
          {/* Download dropdown */}
          <DropdownMenu
            open={isDownloadDropdownOpen}
            onOpenChange={setIsDownloadDropdownOpen}
            trigger={
              <Button
                leftIcon={Download}
                rightIcon={ChevronDown}
                variant='secondary'
              >
                Download
              </Button>
            }
          >
            <DropdownMenu.Item
              leftIcon={BarChart3}
              onSelect={handleDownloadProjections}
            >
              <div>
                <div>Download Projections CSV</div>
                <div className='text-xs text-gray-500'>All projections for {selectedYear}</div>
              </div>
            </DropdownMenu.Item>
            <DropdownMenu.Item
              leftIcon={FileText}
              onSelect={handleDownloadTemplate}
            >
              <div>
                <div>Download CSV Template</div>
                <div className='text-xs text-gray-500'>Template with all carriers</div>
              </div>
            </DropdownMenu.Item>
          </DropdownMenu>

          {/* Replace projections button */}
          <Button
            leftIcon={Upload}
            variant='secondary'
            onClick={handleReplaceProjectionsClick}
          >
            Replace Projections
          </Button>

          {/* Generate projections button */}
          <Button
            leftIcon={Zap}
            variant='primary'
            onClick={handleGenerateProjections}
          >
            Generate Projected Prices
          </Button>
        </div>
      </div>

      {/* File upload section */}
      {showFileUpload && (
        <div className='bg-white shadow rounded-lg p-6'>
          <div className='flex items-center justify-between mb-4'>
            <h2 className='text-lg font-medium text-gray-900'>Upload Projections File</h2>
            <Button
              variant='tertiary'
              onClick={() => setShowFileUpload(false)}
            >
              Cancel
            </Button>
          </div>
          <FileUpload
            accept='.csv'
            onChange={handleFileUpload}
            label='Select CSV file'
            description={`Upload a CSV file with price projections for ${selectedYear}. Expected columns: state, carrier_hios_id, projected_rate_mult`}
          >
            <FileUpload.Content />
          </FileUpload>
        </div>
      )}

      <MedicalPlanProjectionsTable year={selectedYear} />
    </div>
  )
}

export default MedicalPlanProjections
